import React, { useCallback, useEffect, useState, useContext } from 'react';
import { Offcanvas } from 'react-bootstrap';

// Create a context to track a stack of open offcanvases,
// each represented as an object with an id and its onHide callback.
const OffcanvasContext = React.createContext({
  offcanvasStack: [],
  pushOffcanvas: () => {},
  popOffcanvas: () => {},
});

export const OffcanvasProvider = ({ children }) => {
  const [offcanvasStack, setOffcanvasStack] = useState([]);

  const pushOffcanvas = useCallback((item) => {
    setOffcanvasStack((prevStack) => [...prevStack, item]);
  }, []);

  const popOffcanvas = useCallback((id) => {
    setOffcanvasStack((prevStack) => prevStack.filter((item) => item.id !== id));
  }, []);

  // When the persistent backdrop is clicked, call onHide for the top-most offcanvas.
  const handleBackdropClick = () => {
    if (offcanvasStack.length > 0) {
      const topOffcanvas = offcanvasStack[offcanvasStack.length - 1];
      if (topOffcanvas && typeof topOffcanvas.onHide === 'function') {
        topOffcanvas.onHide();
      }
    }
  };

  return (
    <OffcanvasContext.Provider value={{ offcanvasStack, pushOffcanvas, popOffcanvas }}>
      {children}
      {/* Render the persistent backdrop only if there's at least one offcanvas open */}
      {offcanvasStack.length > 0 && (
        <div className="persistent-backdrop" onClick={handleBackdropClick} />
      )}
    </OffcanvasContext.Provider>
  );
};

// Custom Offcanvas component that uses the stack to ensure only one backdrop is used.
const CustomOffcanvas = ({
  show,
  onHide,
  children,
  placement = "end",
  style = {},
  onExited,
  onEntered,
  ...props
}) => {
  const { pushOffcanvas, popOffcanvas } = useContext(OffcanvasContext);
  const id = React.useId();

  useEffect(() => {
    // When the offcanvas is shown, register its id and onHide callback.
    if (show) {
      pushOffcanvas({ id, onHide });
    }
    // Clean up when the component unmounts or show changes.
    return () => {
      popOffcanvas(id);
    };
  }, [show, id, pushOffcanvas, popOffcanvas, onHide]);

  // Ensure removal from the stack when the exit animation completes.
  const handleExited = () => {
    popOffcanvas(id);
    if (onExited) onExited();
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement={placement}
      style={style}
      onExited={handleExited}
      onEntered={onEntered}
      backdrop={false} // Disable the individual backdrop; the provider manages it.
      {...props}
    >
      {children}
    </Offcanvas>
  );
};

export default CustomOffcanvas;
